import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CheckCircle2, Loader2, ShieldCheck, ArrowLeft, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DepositPage — the customer-facing page linked from the approval email.
 * Shows the booking summary and lets the customer pay the deposit via PayPal
 * (funds land in the merchant's PayPal Business account). Stripe remains as
 * a fallback via the existing /bookings/:id → checkout flow.
 */
export default function DepositPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: b }, { data: c }] = await Promise.all([
          axios.get(`${API}/public/booking/${bookingId}/deposit-info`),
          axios.get(`${API}/payments/paypal/config`),
        ]);
        if (cancelled) return;
        setBooking(b);
        setConfig(c);
        if (b.payment_status === "paid") setPaid(true);
      } catch (e) {
        setError(e?.response?.data?.detail || "Booking not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  const deposit = Number(booking?.deposit_amount || 0);
  const total = Number(booking?.amount || 0);
  const remaining = Math.max(total - deposit, 0);

  const paypalOptions = useMemo(() => ({
    "client-id": config?.client_id || "sb", // "sb" = sandbox stub if unconfigured
    currency: config?.currency || "USD",
    intent: "capture",
    "disable-funding": "credit,paylater",
  }), [config]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-white/70 px-6">
        <p>{error}</p>
        <button onClick={() => navigate("/")} className="mt-4 text-burgundy underline">Return home</button>
      </div>
    );
  }
  if (paid) {
    return (
      <SuccessScreen booking={booking} onDone={() => navigate("/")} />
    );
  }

  return (
    <section data-testid="deposit-page" className="mx-auto max-w-2xl px-5 py-14">
      <button
        onClick={() => navigate("/")}
        className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-6"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="label-caps text-burgundy mb-2">Secure deposit · Plenty Rydez</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-3">Reserve your ride</h1>
      <p className="text-white/60 text-sm mb-8">
        Your reservation was approved. Complete the 50% deposit to lock it in — the remaining
        balance is due to your driver at pickup.
      </p>

      {/* Booking summary */}
      <div className="glass rounded-2xl p-6 mb-6">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="label-caps text-white/40 text-[10px]">Booking</div>
            <div className="font-mono text-white/80 text-sm">{booking.id}</div>
          </div>
          <div className="label-caps text-lime-300 text-[10px]">Approved · Deposit due</div>
        </div>
        <div className="font-serif text-2xl">{booking.vehicle_name}</div>
        {booking.trip_type && <div className="label-caps text-white/40 text-[10px] mt-1">{booking.trip_type.replace("_", "-")}</div>}
        <div className="text-white/60 text-sm mt-3">
          {booking.pickup} → {booking.dropoff}
        </div>
        <div className="text-white/50 text-sm">{new Date(booking.pickup_datetime).toLocaleString()}</div>

        <div className="mt-5 pt-4 border-t border-white/10 space-y-2">
          <Row label="Estimated total" value={`$${total.toFixed(2)}`} />
          <Row label="Deposit (due now)" value={`$${deposit.toFixed(2)}`} highlight />
          <Row label="Balance due at pickup" value={`$${remaining.toFixed(2)}`} muted />
        </div>
      </div>

      {/* Payment options */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={16} className="text-lime-400" />
          <span className="label-caps text-white/60 text-[11px]">Secure checkout</span>
        </div>

        {!config?.configured && (
          <div className="mb-4 rounded-lg border border-amber-400/30 bg-amber-400/10 text-amber-200 text-xs px-3 py-2">
            PayPal is running in <strong>sandbox mode</strong>. Add your live <code>PAYPAL_CLIENT_ID</code> and
            <code className="mx-1">PAYPAL_SECRET</code> to <code>backend/.env</code> to accept real payments.
          </div>
        )}

        {config && (
          <PayPalScriptProvider options={paypalOptions}>
            <PayPalButtons
              style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }}
              data-testid="paypal-buttons"
              createOrder={async () => {
                try {
                  const { data } = await axios.post(`${API}/payments/paypal/create-order`, { booking_id: booking.id });
                  return data.id;
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not create PayPal order");
                  throw e;
                }
              }}
              onApprove={async (data) => {
                try {
                  const { data: cap } = await axios.post(`${API}/payments/paypal/capture-order/${data.orderID}`);
                  if (cap.status === "COMPLETED") {
                    toast.success("Deposit received — your ride is confirmed");
                    setPaid(true);
                  } else {
                    toast.warning("Payment status: " + cap.status);
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Capture failed");
                }
              }}
              onError={(err) => {
                console.error("PayPal error", err);
                toast.error("PayPal error — please try again or contact us");
              }}
            />
          </PayPalScriptProvider>
        )}

        {/* Fallback / secondary option */}
        <div className="my-5 flex items-center gap-3 text-white/30 text-[10px] label-caps">
          <span className="flex-1 h-px bg-white/10" />
          <span>or</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <button
          type="button"
          data-testid="stripe-fallback"
          onClick={async () => {
            try {
              const origin = window.location.origin;
              const { data } = await axios.post(`${API}/payments/checkout`, {
                booking_id: booking.id, origin_url: origin,
              });
              window.location.href = data.url;
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not start Stripe checkout");
            }
          }}
          className="w-full py-3 rounded-full border border-white/15 text-white/80 hover:bg-white/5 inline-flex items-center justify-center gap-2 label-caps text-xs"
        >
          <CreditCard size={14} /> Pay with card via Stripe
        </button>

        <p className="text-[10px] text-white/40 mt-4 text-center">
          Payments are processed by PayPal & Stripe. Plenty Rydez never sees your card details.
        </p>
      </div>
    </section>
  );
}

function Row({ label, value, highlight, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`label-caps text-[10px] ${muted ? "text-white/30" : "text-white/50"}`}>{label}</span>
      <span className={`text-sm ${highlight ? "text-lime-300 font-medium" : muted ? "text-white/40" : "text-white/85"}`}>
        {value}
      </span>
    </div>
  );
}

function SuccessScreen({ booking, onDone }) {
  return (
    <section data-testid="deposit-success" className="mx-auto max-w-lg px-5 py-16 text-center">
      <div className="mx-auto w-20 h-20 rounded-full bg-lime-400/15 border border-lime-400/40 flex items-center justify-center mb-5">
        <CheckCircle2 size={30} className="text-lime-300" />
      </div>
      <div className="label-caps text-lime-400 text-[10px] mb-1">Confirmed</div>
      <h1 className="font-serif text-3xl mb-3">Your ride is locked in</h1>
      <p className="text-white/60 text-sm mb-6">
        Deposit received for <strong className="text-white">{booking?.vehicle_name}</strong>.
        We&apos;ve emailed a receipt and your driver details will follow shortly.
      </p>
      <button
        onClick={onDone}
        className="px-6 py-3 rounded-full bg-burgundy text-white text-sm label-caps hover:bg-burgundy/80"
      >
        Back to home
      </button>
    </section>
  );
}
